import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import {
  FaPlus,
  FaQrcode,
  FaPowerOff,
  FaInfoCircle,
  FaTrash,
  FaEllipsisH,
  FaPen,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebChannels,
  createWhatsAppWebChannel,
  updateWhatsAppWebChannel,
  startWhatsAppWebSession,
  disconnectWhatsAppWebChannel,
  deleteWhatsAppWebChannel,
} from "../../../../services/api";
import QRModal from "./QRModal";
import DisconnectWarningModal from "./DisconnectWarningModal";
import NewChannelModal from "./NewChannelModal";
import InfoModal from "./InfoModal";
import ActionsModal from "./ActionsModal";

const STATUS_VARIANT = {
  active: "success",
  pending: "warning",
  connecting: "info",
  disconnected: "danger",
  inactive: "secondary",
  error: "danger",
};

const STATUS_LABEL = {
  active: "Ativo",
  pending: "Pendente",
  connecting: "Conectando",
  disconnected: "Desconectado",
  inactive: "Inativo",
  error: "Erro",
};

export default function ChannelsPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasActive, setHasActive] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);

  const [qrChannel, setQrChannel] = useState(null);
  const [warning, setWarning] = useState(null);
  const [infoChannel, setInfoChannel] = useState(null);
  const [actionsChannel, setActionsChannel] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppWebChannels();
      setChannels(res?.data?.channels ?? []);
      setHasActive(!!res?.data?.hasActiveChannel);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Polling de status global a cada 10s
  useEffect(() => {
    const i = setInterval(() => reload(), 10000);
    return () => clearInterval(i);
  }, [reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  async function handleCreate(payload) {
    setCreating(true);
    try {
      await createWhatsAppWebChannel(payload);
      setShowNew(false);
      await reload();
    } finally {
      setCreating(false);
    }
  }

  async function handleNewQR(channel) {
    const res = await startWhatsAppWebSession(channel.id);
    if (res?.data?.shouldWarn) {
      setWarning({ channel, minutes: res.data.minutesSinceDisconnect });
      return;
    }
    setQrChannel(channel);
  }

  function handleWarningContinue() {
    const ch = warning.channel;
    setWarning(null);
    setQrChannel(ch);
  }

  async function handleDisconnect(channel) {
    if (!window.confirm(`Desconectar "${channel.name}"?`)) return;
    await disconnectWhatsAppWebChannel(channel.id);
    reload();
  }

  async function handleDelete(channel) {
    if (channel.status === "active") {
      alert("Desconecte o canal antes de excluir.");
      return;
    }
    if (!window.confirm(`Excluir canal "${channel.name}"?`)) return;
    await deleteWhatsAppWebChannel(channel.id);
    reload();
  }

  function startEditing(channel) {
    setEditingId(channel.id);
    setEditingName(channel.name);
  }

  function cancelEditing() {
    setEditingId(null);
    setEditingName("");
  }

  async function saveEdit(channel) {
    const name = editingName.trim();
    if (!name || name === channel.name) {
      cancelEditing();
      return;
    }
    try {
      await updateWhatsAppWebChannel(channel.id, { name });
      cancelEditing();
      reload();
    } catch (err) {
      alert("Falha ao renomear canal.");
    }
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          {/* Top status chip — replica OrbitSender */}
          <div className="mb-3">
            <Badge
              variant={hasActive ? "success" : "danger"}
              style={{ padding: "8px 12px", fontSize: 13 }}
            >
              ● {hasActive ? "Conectado" : "Desconectado"}
            </Badge>
          </div>

          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-1">Canais</h2>
              <p className="text-muted mb-0">Gerencie seus canais de WhatsApp</p>
            </div>
            <Button variant="primary" onClick={() => setShowNew(true)}>
              <FaPlus /> Novo Canal
            </Button>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : channels.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <div style={{ fontSize: 48 }}>💬</div>
                <h4 className="mt-3">Nenhum canal encontrado</h4>
                <p className="text-muted">
                  Crie seu primeiro canal para começar a usar o WhatsApp
                </p>
                <Button variant="primary" onClick={() => setShowNew(true)}>
                  <FaPlus /> Criar Canal
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {channels.map((ch) => (
                <Col key={ch.id} md={6} lg={4} className="mb-3">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          {editingId === ch.id ? (
                            <InputGroup size="sm">
                              <Form.Control
                                autoFocus
                                value={editingName}
                                onChange={(e) =>
                                  setEditingName(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit(ch);
                                  if (e.key === "Escape") cancelEditing();
                                }}
                              />
                              <InputGroup.Append>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => saveEdit(ch)}
                                >
                                  <FaCheck />
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={cancelEditing}
                                >
                                  <FaTimes />
                                </Button>
                              </InputGroup.Append>
                            </InputGroup>
                          ) : (
                            <h5 className="mb-0 d-flex align-items-center">
                              <span style={{ color: "#25D366" }}>📱</span>
                              <span className="ml-2">{ch.name}</span>
                              <FaPen
                                className="ml-2 text-muted"
                                style={{ cursor: "pointer", fontSize: 12 }}
                                onClick={() => startEditing(ch)}
                                title="Renomear canal"
                              />
                            </h5>
                          )}
                          <small className="text-muted">
                            QR Code · persona: {ch.device_name}
                          </small>
                        </div>
                        <Badge
                          variant={STATUS_VARIANT[ch.status] || "secondary"}
                        >
                          ● {STATUS_LABEL[ch.status] || ch.status}
                        </Badge>
                      </div>

                      <hr />
                      <small className="text-muted">
                        📅 Criado:{" "}
                        {new Date(ch.created_at).toLocaleDateString("pt-BR")}
                      </small>

                      <div
                        className="d-flex flex-wrap mt-3"
                        style={{ gap: 6 }}
                      >
                        {ch.status === "active" ? (
                          <>
                            <Button
                              size="sm"
                              variant="info"
                              onClick={() => setInfoChannel(ch)}
                            >
                              <FaInfoCircle /> Informações
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDisconnect(ch)}
                            >
                              <FaPowerOff /> Desconectar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setActionsChannel(ch)}
                            >
                              <FaEllipsisH /> Ações
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleNewQR(ch)}
                            >
                              <FaQrcode /> Novo QR Code
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDelete(ch)}
                            >
                              <FaTrash />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      <NewChannelModal
        show={showNew}
        saving={creating}
        onClose={() => setShowNew(false)}
        onCreate={handleCreate}
      />

      <DisconnectWarningModal
        show={!!warning}
        minutesSinceDisconnect={warning?.minutes}
        onCancel={() => setWarning(null)}
        onContinue={handleWarningContinue}
      />

      <QRModal
        show={!!qrChannel}
        channel={qrChannel}
        onClose={() => {
          setQrChannel(null);
          reload();
        }}
        onConnected={() => {
          setTimeout(() => {
            setQrChannel(null);
            reload();
          }, 1500);
        }}
      />

      <InfoModal
        show={!!infoChannel}
        channel={infoChannel}
        onClose={() => setInfoChannel(null)}
      />

      <ActionsModal
        show={!!actionsChannel}
        channel={actionsChannel}
        onClose={() => setActionsChannel(null)}
      />
    </Container>
  );
}
